<template>
  <div class="col-md-12">
    <div class="row">
      <div class="col-md-6">
        <VueInput
          :error="errors.id"
          label="Fabric ID"
          v-model="modelValues.id"
          placeholder="Fabric ID"
          type="number"
        />
      </div>
      <div class="col-md-6">
        <div class="form-group row">
          <label class="col-sm-5 col-form-label">Pattern</label>
          <div class="col-sm-7">
            <v-select
              :options="fieldValues.patterns"
              v-model="modelValues.pattern"
              placeholder="Choose One"
              v-if="!switchToManualPatternInput"
            />
            <input
              v-if="switchToManualPatternInput && !isReadOnlyUser"
              type="text"
              class="form-control"
              v-model="modelValues.pattern"
              @change="newPatternSubmitted"
              placeholder="Type new pattern name"
            />
            <p
              class="input-switcher"
              @click="switchToManualPatternInput = !switchToManualPatternInput"
              v-if="!isReadOnlyUser"
            >
              {{
                switchToManualPatternInput
                  ? "Select existing pattern"
                  : "Create new pattern"
              }}
            </p>
            <small v-if="errors.pattern" class="form-text text-danger">{{
              errors.pattern.join(" ")
            }}</small>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6">
        <VueInput
          :error="errors.name"
          label="Name"
          v-model="modelValues.name"
          placeholder="Name"
        />
      </div>
      <div class="col-md-6">
        <div class="form-group row">
          <label class="col-sm-5 col-form-label">Weave</label>
          <div class="col-sm-7">
            <v-select
              :options="fieldValues.weaves"
              v-model="modelValues.default_weave"
              placeholder="Choose One"
              v-if="!switchToManualWeaveInput"
            />
            <input
              v-if="switchToManualWeaveInput && !isReadOnlyUser"
              type="text"
              class="form-control"
              v-model="modelValues.default_weave"
              @change="newWeaveSubmitted"
              placeholder="Type new weave name"
            />
            <p
              class="input-switcher"
              @click="switchToManualWeaveInput = !switchToManualWeaveInput"
              v-if="!isReadOnlyUser"
            >
              {{
                switchToManualWeaveInput
                  ? "Select existing weave"
                  : "Create new weave"
              }}
            </p>
            <small v-if="errors.default_weave" class="form-text text-danger">{{
              errors.default_weave.join(" ")
            }}</small>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6">
        <div class="form-group row">
          <label class="col-sm-5 col-form-label">Composition label</label>
          <div class="col-sm-7">
            <v-select
              :options="fieldValues.formattedCompositionLabels"
              v-model="modelValues.fabric_composition_label_id"
              placeholder="Choose One"
              label="name"
              :reduce="(compositionLabel) => compositionLabel.id"
            />
            <small
              v-if="errors.fabric_composition_label_id"
              class="form-text text-danger"
              >{{ errors.fabric_composition_label_id.join(" ") }}</small
            >
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group row">
          <label class="col-sm-5 col-form-label">Class</label>
          <div class="col-sm-7">
            <v-select
              :options="fieldValues.classes"
              v-model="modelValues.fabric_class"
              placeholder="Choose One"
            />
            <small v-if="errors.fabric_class" class="form-text text-danger">{{
              errors.fabric_class.join(" ")
            }}</small>
          </div>
        </div>
      </div>
    </div>

    <div class="row bottom-spacing" v-if="$auth.user().role === 'admin'">
      <div class="col-md-6">
      </div>
      <div class="col-md-6">
        <div class="form-group row">
          <label class="col-sm-5 col-form-label">Add-on</label>
          <div class="col-sm-7">
            <v-select
              :options="fieldValues.add_ons"
              v-model="modelValues.add_on_group"
              placeholder="Choose One"
            />
            <input
              v-if="switchToManualAddOnInput"
              type="text"
              class="form-control"
              v-model="modelValues.add_on_group"
              @change="newAddOnSubmitted"
              placeholder="Type new add-on name, use text, underscore and dash only"
            />
            <p
              class="input-switcher"
              @click="switchToManualAddOnInput = !switchToManualAddOnInput"
            >
              {{
                switchToManualAddOnInput
                  ? "Select existing add-on"
                  : "Create new add-on"
              }}
            </p>
            <small v-if="errors.add_on_group" class="form-text text-danger">{{
              errors.add_on_group.join(" ")
            }}</small>
          </div>
        </div>
      </div>
    </div>

    <div class="row" v-if="$auth.user().role === 'admin'">
      <div class="col-md-6">
        <div class="form-group row">
          <label class="col-sm-5 col-form-label">Fiber 1</label>
          <div class="col-sm-7">
            <v-select
              :options="fieldValues.fibers"
              v-model="modelValues.fiber_1"
              @input="checkIfResetPercentageRequired(1)"
              placeholder="Choose One"
              v-if="!switchToManualFiberInputs[1]"
            />
            <input
              v-if="switchToManualFiberInputs[1] && !isReadOnlyUser"
              type="text"
              class="form-control"
              v-model="modelValues.fiber_1"
              @change="event => newFiberSubmitted(event, 1)"
              placeholder="Type new fiber name"
            />
            <p
              class="input-switcher"
              @click="switchToManualFiberInputs[1] = !switchToManualFiberInputs[1]"
              v-if="!isReadOnlyUser"
            >
              {{
                switchToManualFiberInputs[1]
                  ? "Select existing fiber"
                  : "Create new fiber"
              }}
            </p>
            <small v-if="errors.fiber_1" class="form-text text-danger">{{
              errors.fiber_1.join(" ")
            }}</small>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="row">
          <div class="col-md-9 slider-container">
            <vue-slider
              v-model="modelValues.fiber_1_percentage"
              :min="0"
              :max="100"
            />
            <small
              v-if="errors.fiber_1_percentage"
              class="form-text text-danger"
              >{{ errors.fiber_1_percentage.join(" ") }}</small
            >
          </div>
          <div class="col-md-3">
            <input
              type="number"
              class="form-control slider-value"
              v-model="modelValues.fiber_1_percentage"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="row" v-if="$auth.user().role === 'admin'">
      <div class="col-md-6">
        <div class="form-group row">
          <label class="col-sm-5 col-form-label">Fiber 2</label>
          <div class="col-sm-7">
            <v-select
              :options="fieldValues.fibers"
              v-model="modelValues.fiber_2"
              @input="checkIfResetPercentageRequired(2)"
              placeholder="Choose One"
              v-if="!switchToManualFiberInputs[2]"
            />
            <input
              v-if="switchToManualFiberInputs[2] && !isReadOnlyUser"
              type="text"
              class="form-control"
              v-model="modelValues.fiber_2"
              @change="event => newFiberSubmitted(event, 2)"
              placeholder="Type new fiber name"
            />
            <p
              class="input-switcher"
              @click="switchToManualFiberInputs[2] = !switchToManualFiberInputs[2]"
              v-if="!isReadOnlyUser"
            >
              {{
                switchToManualFiberInputs[2]
                  ? "Select existing fiber"
                  : "Create new fiber"
              }}
            </p>
            <small v-if="errors.fiber_2" class="form-text text-danger">{{
              errors.fiber_2.join(" ")
            }}</small>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="row">
          <div class="col-md-9 slider-container">
            <vue-slider
              v-model="modelValues.fiber_2_percentage"
              :min="0"
              :max="100"
            />
            <small
              v-if="errors.fiber_2_percentage"
              class="form-text text-danger"
              >{{ errors.fiber_2_percentage.join(" ") }}</small
            >
          </div>
          <div class="col-md-3">
            <input
              type="number"
              class="form-control slider-value"
              v-model="modelValues.fiber_2_percentage"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="row" v-if="$auth.user().role === 'admin'">
      <div class="col-md-6">
        <div class="form-group row">
          <label class="col-sm-5 col-form-label">Fiber 3</label>
          <div class="col-sm-7">
            <v-select
              :options="fieldValues.fibers"
              v-model="modelValues.fiber_3"
              @input="checkIfResetPercentageRequired(3)"
              placeholder="Choose One"
              v-if="!switchToManualFiberInputs[3]"
            />
            <input
              v-if="switchToManualFiberInputs[3] && !isReadOnlyUser"
              type="text"
              class="form-control"
              v-model="modelValues.fiber_3"
              @change="event => newFiberSubmitted(event, 3)"
              placeholder="Type new fiber name"
            />
            <p
              class="input-switcher"
              @click="switchToManualFiberInputs[3] = !switchToManualFiberInputs[3]"
              v-if="!isReadOnlyUser"
            >
              {{
                switchToManualFiberInputs[3]
                  ? "Select existing fiber"
                  : "Create new fiber"
              }}
            </p>
            <small v-if="errors.fiber_3" class="form-text text-danger">{{
              errors.fiber_3.join(" ")
            }}</small>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="row">
          <div class="col-md-9 slider-container">
            <vue-slider
              v-model="modelValues.fiber_3_percentage"
              :min="0"
              :max="100"
            />
            <small
              v-if="errors.fiber_3_percentage"
              class="form-text text-danger"
              >{{ errors.fiber_3_percentage.join(" ") }}</small
            >
          </div>
          <div class="col-md-3">
            <input
              type="number"
              class="form-control slider-value"
              v-model="modelValues.fiber_3_percentage"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueSlider from "vue-slider-component";
import VueInput from "@/pages/Tools/input";

export default {
  components: {
    VueSlider,
    VueInput,
  },
  props: {
    fieldValues: {
      type: [Object],
      default: {},
    },
    modelValues: {
      type: [Object],
      default: {},
    },
    errors: {
      type: [Object],
      default: {},
    },
    isReadOnlyUser: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      switchToManualPatternInput: false,
      switchToManualWeaveInput: false,
      switchToManualClassInput: false,
      switchToManualAddOnInput: false,
      switchToManualFiberInputs: {
        1: false,
        2: false,
        3: false,
      },
    };
  },
  methods: {
    checkIfResetPercentageRequired(fiber) {
      if (this.modelValues["fiber_" + fiber] == null) {
        this.modelValues["fiber_' + fiber + '_percentage"] = null;
      }
    },
    async newPatternSubmitted(event) {
      const label = event.target.value;
      await this.createNewFabricProperty(
        "pattern",
        label,
        this.fieldValues.patterns
      );
      this.switchToManualPatternInput = false;
    },
    async newWeaveSubmitted(event) {
      const label = event.target.value;
      await this.createNewFabricProperty(
        "weave",
        label,
        this.fieldValues.weaves
      );
      this.switchToManualWeaveInput = false;
    },
    async newAddOnSubmitted() {
      const label = event.target.value;
      await this.createNewFabricProperty(
        "add_on",
        label,
        this.fieldValues.add_ons
      );
      this.switchToManualAddOnInput = false;
    },
    async newFiberSubmitted(event, fiberNumber) {
      const label = event.target.value;
      await this.createNewFabricProperty(
        "fiber",
        label,
        this.fieldValues.fibers
      );
      this.switchToManualFiberInputs[fiberNumber] = false;
    },
    /**
     *
     * @param option
     * @param label
     * @param existingLabels
     * @returns {Promise<void>}
     */
    async createNewFabricProperty(fabricProperty, label, existingLabels) {
      const formData = new FormData();
      formData.append("label", label);
      formData.append("fabric_property", fabricProperty);
      if (label) {
        let index = existingLabels.findIndex((x) => x === label);
        // Save the option if it does not exist yet
        if (index === -1) {
          existingLabels.push(label);
          try {
            await axios.post("v2/fabrics/property", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            toastr.success("Successfully created");
          } catch (error) {
            if (error.response.status === 403) {
              toastr.error(
                "Not authorized, please ensure that you have the required permissions."
              );
            } else {
              const errorMessage = error.response.data.message;
              toastr.error("An error occurred: " + errorMessage);
            }
          }
        } else {
          toastr.error("That option already exists");
        }
      }
    },
  },
};
</script>

<style scoped>
.input-switcher {
  position: absolute;
  /*bottom: -28px;*/
  left: 20px;
  font-size: 12px;
  color: var(--primary-color);
  cursor: pointer;
}
.bottom-spacing {
  margin-bottom: 10px;
}
</style>
